<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2 py-md-0"
    >
      <!-- LEAD PROFILE PICTURE -->
      <div
        v-if="!isMobile()"
        class="position-absolute px-1 d-none d-md-block"
        :style="{
          marginLeft: showCustomerSuggestion ? '430px' : '200px',
          marginTop: '-26px'
        }"
      >
        <BButton
          size="sm"
          variant="link"
          class="text-dark"
          @click.prevent="showOrHideCustomerSuggestions"
        >
          {{ showCustomerSuggestion ? 'Hide' : 'Show' }} Customer Suggestions by Municipality
        </BButton>
      </div>
      <BCol
        v-if="showCustomerSuggestion || isMobile()"
        cols="12"
        md="2"
        style="margin-top: 24px;"
        order="2"
        order-md="1"
      >
        <CustomerSuggestions
          ref="customerSuggestions"
          :customer="customerProfile"
        />
      </BCol>

      <BCol
        cols="12"
        :md="showCustomerSuggestion ? 10 : 12"
        order="1"
        order-md="2"
      >
        <BRow class="gap-y-2">
          <BCol
            cols="12"
          >
            <CustomerProfilePictures
              ref="customerProfilePictures"
              :loading="state.busy"
              :customer="customerProfile"
              @updated="changedProfile"
            />
          </BCol>

          <!-- LEAD INFORMATIONS -->
          <BCol
            cols="12"
          >
            <BOverlay
              :show="state.busy"
              opacity="0.5"
            >
              <BCard
                class="border min-h-550px"
              >
                <BCardText>
                  <BTabs
                    v-model="currentTabIndex"
                    nav-class="scrollable-x-tabs no-scrollbar nav-customer-tabs gap-x-1"
                    pills
                  >
                    <template #tabs-start>
                      <div class="nav-item">
                        <div
                          class="d-flex align-items-center position-absolute"
                          style="padding: 2px 1px; height: 38px;"
                        >
                          <i
                            class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                            style="font-size: 20px;"
                          />
                        </div>
                      </div>
                    </template>
                    <template #tabs-end>
                      <div class="nav-item">
                        <div
                          class="d-flex align-items-center position-absolute"
                          style="right: 20px; padding: 2px 1px; height: 38px;"
                        >
                          <i
                            class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                            style="font-size: 20px;"
                          />
                        </div>
                      </div>
                    </template>
                    <BTab>
                      <template #title>
                        <i :class="iconState('fa-file-user', currentTabIndex === 0)" /> Info
                      </template>

                      <BContainer
                        class="h-100 px-2"
                        fluid
                      >
                        <CustomerInformations
                          ref="customerInformations"
                        />

                        <div
                          v-if="!isVerified"
                          class="mt-1 d-flex gap-2 justify-content-end justify-content-md-start"
                        >
                          <BButton
                            size="sm"
                            variant="primary"
                            :disabled="isFetchingInformationLocations()"
                            @click.prevent="updateInformation"
                          >
                            Update Information
                          </BButton>
                          <BButton
                            size="sm"
                            variant="secondary"
                            :disabled="isFetchingInformationLocations()"
                            @click.prevent="createDocument"
                          >
                            Mark As Verified
                          </BButton>
                        </div>
                      </BContainer>
                    </BTab>

                    <BTab lazy>
                      <template #title>
                        <i :class="iconState('fa-history', currentTabIndex === 1)" /> Interactions
                      </template>

                      <BContainer
                        class="h-100 p-0"
                        fluid
                      >
                        <CustomerInteractions
                          ref="customerInteractions"
                          :customer="customerProfile"
                        />
                      </BContainer>
                    </BTab>

                    <BTab lazy>
                      <template #title>
                        <i :class="iconState('fa-file-zipper', currentTabIndex === 4)" /> Documents
                      </template>
                      <BContainer
                        class="h-100 p-0"
                        fluid
                      >
                        <CustomerDocuments
                          ref="customerDocuments"
                          :customer="customerProfile"
                          @updated="updateDocuments"
                        />
                      </BContainer>
                    </BTab>
                  </BTabs>
                </BCardText>
              </BCard>
            </BOverlay>
          </BCol>

          <BCol
            cols="12"
            md="4"
          >
            <BCard class="border">
              <BCardText>
                <div class="d-flex gap-4">
                  <BAvatar
                    v-if="officer.photo_url"
                    :src="computedProfilePath(officer.photo_url)"
                    variant="primary"
                    size="36px"
                  />
                  <BAvatar
                    v-else
                    variant="primary"
                    size="36px"
                    :text="String(officer?.first_name).charAt(0) + String(officer?.last_name).charAt(0)"
                  />
                  <div class="d-flex flex-column justify-content-center">
                    <div
                      class="font-weight-bolder"
                      style="font-size: 0.8rem;"
                    >
                      {{ officer?.first_name }} {{ officer?.last_name }}
                    </div>
                    <div
                      class="mt-1 text-dark"
                      style="font-size: 0.7rem;"
                    >
                      Creator
                    </div>
                  </div>
                </div>
              </BCardText>
            </BCard>
          </BCol>

          <BCol
            v-if="areaManager?.id"
            cols="12"
            md="4"
          >
            <BCard class="border">
              <BCardText>
                <div class="d-flex gap-4">
                  <BAvatar
                    v-if="areaManager.photo_url"
                    :src="computedProfilePath(areaManager.photo_url)"
                    variant="primary"
                    size="36px"
                  />
                  <BAvatar
                    v-else
                    variant="primary"
                    size="36px"
                    :text="String(areaManager?.first_name).charAt(0) + String(areaManager?.last_name).charAt(0)"
                  />
                  <div class="d-flex flex-column justify-content-center">
                    <div
                      class="font-weight-bolder"
                      style="font-size: 0.8rem;"
                    >
                      {{ areaManager?.first_name }} {{ areaManager?.last_name }}
                    </div>
                    <div
                      class="mt-1 text-dark"
                      style="font-size: 0.7rem;"
                    >
                      Verifier
                    </div>
                  </div>
                </div>
              </BCardText>
            </BCard>
          </BCol>
        </BRow>
      </BCol>
    </BRow>

    <BModal
      ref="modalDocument"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
    >
      <div class="p-2 min-h-400px max-h-400px">
        <ValidationObserver
          ref="formDocument"
        >
          <form
            role="form"
            novalidate
            @submit.prevent
          >
            <BRow>
              <BCol
                cols="12"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="document_type_id"
                  name="document type"
                  rules="required"
                >
                  <BFormGroup
                    label="Document Type"
                    label-for="fDocumentType"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fDocumentType"
                      v-model="formDocument.document_type_name"
                      placeholder="no selected document type"
                      disabled
                      :state="errors.length > 0 ? false : null"
                    />
                    <div
                      v-if="(errors.length > 0)"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>
            </BRow>
          </form>
        </ValidationObserver>

        <CustomerDocumentUploader
          ref="attachmentUploader"
          :path="formDocument.attachment_path"
          :files="formDocument.attachments"
          :loading="state.busy"
          :show-uploader="!state.editing"
          @onUploaded="updateFormDocument"
        />
      </div>
      <template #modal-footer="{ cancel }">
        <BButton
          v-if="!state.editing"
          variant="secondary"
          :disabled="state.busy || fetching.documentTypes"
          @click.prevent="validateDocument"
        >
          Mark As Verified
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          {{ state.editing ? 'Close' : 'Discard' }}
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'

import CustomerProfilePictures from '@/views/admin/customer-management/ProfilePictures.vue'
import CustomerInformations from '@/views/admin/customer-management/Informations.vue'
import CustomerInteractions from '@/views/admin/customer-management/interactions/Base.vue'
import CustomerDocuments from '@/views/admin/customer-management/documents/Base.vue'
import CustomerSuggestions from '@/views/admin/customer-management/Suggestions.vue'
import CustomerDocumentUploader from '@/views/admin/customer-management/documents/Uploader.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MDocumentTypes from '@/mixins/maintenances/document-types'

import { SAdminCustomerDocument } from '@/services/admin'

export default {
  name: 'VAdminCustomerManagement',

  components: {
    CustomerDocumentUploader,
    CustomerProfilePictures,
    CustomerInformations,
    CustomerInteractions,
    CustomerDocuments,
    CustomerSuggestions
  },

  mixins: [
    MxMisc,
    MxFormatter,
    MDocumentTypes
  ],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({
    title: 'Customer Management'
  }),

  data () {
    return {
      formErrors: {
        documents: []
      },
      state: {
        busy: false
      },
      currentTabIndex: 0,
      tasksCurrentTabIndex: 0,
      formDocument: {
        customer_id: '',
        document_type_id: '',
        document_type_name: '',
        attachment_path: '',
        attachments: [],
        active: 1
      },
      areaManager: {
        id: '',
        first_name: '',
        last_name: '',
        photo_url: ''
      },
      officer: {
        id: '',
        first_name: '',
        last_name: '',
        photo_url: ''
      },
      customerProfile: {
        id: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        suffix_name: '',
        mobile_number: '',
        birth_date: '',
        gender: '',
        address: '',
        region_id: '',
        province_id: '',
        municipality_id: '',
        barangay_id: '',
        // cluster_id: '',
        status: 'New',
        documents: [],
        photos: [],
        farms: [],
        crops: [],
        is_blo: 0,
        is_aljay: 0,
        active: 1
      },
      formCustomerProfilePicture: {
        file: null
      },
      customerAttachments: [],
      customerInteractions: [],
      customerTasks: []
    }
  },

  computed: Object.assign({
    isVerified () {
      return this.customerProfile.status === 'Verified'
    }
  },
  mapGetters({
    showCustomerSuggestion: 'customer-management/showCustomerSuggestionState',
    currentUser: 'auth/user'
  })),

  watch: {
    '$route.params.customer' () {
      this.getCustomerProfile(true, false)
      this.currentTabIndex = 0
    },

    'customerProfile.municipality_id' () {
      if (this.$refs.customerSuggestions) {
        this.$refs.customerSuggestions.tableProvider()
      }
    },

    'selected.documentType' (documentType) {
      this.formDocument.document_type_id = documentType?.id || null
      this.formDocument.document_type_name = documentType?.document_type_name || null
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getCustomerProfile()

      this.$nextTick(() => {
        this.attachArrowScroller('.nav-customer-tabs')
      })
    }
  },

  destroyed () {
    this.detachArrowScroller('.nav-customer-tabs')
  },

  methods: {
    computedProfilePath (photoUrl) {
      return `${process.env.VUE_APP_API_BASE_URL}/${photoUrl}`
    },

    isFetchingInformationLocations () {
      if (this.$refs.customerInformations) {
        return this.$refs.customerInformations.isFetchingLocations()
      }

      return false
    },

    vTabDocuments () {
      return this.formErrors.documents.length > 0
    },

    resetDocumentErrors () {
      this.formErrors.documents = []
    },

    showOrHideCustomerSuggestions () {
      this.showCustomerSuggestions(!this.showCustomerSuggestion)

      this.$nextTick(() => {
        if (this.showCustomerSuggestion) {
          this.$refs.customerSuggestions.tableProvider()
        }
      })
    },

    setCustomerProfile (customer) {
      this.formDocument = Object.assign(this.formDocument, {
        customer_id: customer.id,
        document_type_id: 1,
        document_type_name: 'Consent Form',
        attachment_path: '',
        attachments: [],
        active: 1
      })

      this.customerProfile = Object.assign(this.customerProfile, {
        id: customer.id,
        // cluster_id: customer.cluster_id,
        region_id: customer.region_id,
        province_id: customer.province_id,
        municipality_id: customer.municipality_id,
        barangay_id: customer.barangay_id,
        first_name: customer.first_name,
        middle_name: customer.middle_name,
        last_name: customer.last_name,
        suffix_name: customer.suffix_name,
        email: customer.email,
        mobile_number: customer.mobile_number,
        birth_date: customer.birth_date,
        gender: customer.gender,
        address: customer.address,
        status: customer.status,
        documents: customer?.documents || [],
        farms: customer?.farms || [],
        crops: customer?.crops || [],
        photos: customer?.photos || [],
        is_blo: customer.is_blo || 0,
        is_aljay: customer.is_aljay || 0,
        active: customer.active
      })
    },

    async getCustomerProfile (useLoader = true, useTableProvider = true) {
      this.state.busy = useLoader && true

      this.$refs.customerInformations.showInformation(this.$route.params.customer).then(
        async customer => {
          if (!customer) {
            return this.swalInvalid(
              '<h6>Your customer profile seems to be incorrect or incomplete, please check and try again.</h6>',
              'Customer Not Found'
            ).then(
              () => {
                this.$router.push({ name: 'fso.customers' })
              }
            )
          }

          this.areaManager = Object.assign(this.areaManager, {
            id: customer.verifier?.id,
            first_name: customer.verifier?.first_name,
            last_name: customer.verifier?.last_name,
            photo_url: customer.verifier?.photo_url
          })

          this.officer = Object.assign(this.officer, {
            id: customer.creator?.id,
            first_name: customer.creator?.first_name,
            last_name: customer.creator?.last_name,
            photo_url: customer.creator?.photo_url
          })

          this.$refs.customerInformations.editInformation(customer)
          this.setCustomerProfile(customer)
        }
      ).finally(() => {
        if (useTableProvider && (this.showCustomerSuggestion || this.isMobile())) {
          this.$refs.customerSuggestions.tableProvider()
        }

        this.state.busy = false
      })
    },

    updateInformation () {
      this.$refs.customerInformations.validateInformation().then(
        () => {
          this.swalConfirm({
            text: 'Update the Information of this Customer?',
            confirmButtonText: 'Update',
            cancelButtonText: 'Discard',
            preConfirm: async () => {
              return this.$refs.customerInformations.putInformation().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    this.$refs.customerInformations.setErrors(message.errors)
                  }
                }
              )
            }
          }).then(
            ({ value }) => {
              if (value?.customer && value?.message) {
                const { message, customer } = value
                this.swalSuccess(message).then(() => {
                  this.setCustomerProfile(customer)
                })
              }
            }
          )
        }
      ).catch(() => {
        this.swalInvalid()
      })
    },

    changedProfile (customerPhoto) {
      this.customerProfile.photos = [customerPhoto]
    },

    updateDocuments (documents) {
      this.customerProfile.documents = [documents]
    },

    createDocument () {
      this.formDocument = Object.assign(this.formDocument, {
        id: null,
        customer_id: this.customerProfile.id,
        attachment_path: '',
        attachments: [],
        document_type_id: 1,
        document_type_name: 'Consent Form',
        active: 1
      })

      this.$refs.modalDocument.show()
    },

    updateFormDocument (attachment) {
      this.formDocument = Object.assign(this.formDocument, {
        attachment_path: attachment.attachment_path,
        attachments: attachment.attachments
      })
    },

    async validateDocument () {
      await this.$refs.formDocument.validate().then(
        async validDocument => {
          const noAttachments = this.formDocument.attachments.length === 0

          if (!validDocument || noAttachments) {
            if (noAttachments) {
              this.$refs.attachmentUploader.setErrors({
                attachments: ['Please upload at least one attachment.']
              })
            }

            return this.swalInvalid()
          }

          this.swalConfirm({
            text: 'Mark this Customer as Verified?',
            confirmButtonText: 'Mark as Verified',
            cancelButtonText: 'Discard',
            customClass: {
              confirmButton: 'btn btn-sm btn-secondary'
            },
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return await this.postDocument()
              }

              return createOrUpdate().then(() => {
                this.$refs.customerInformations.setInformation({ status: 'Verified' })

                return this.$refs.customerInformations.putInformation().then(
                  ({ message, customer }) => {
                    this.swalSuccess(message).then(() => {
                      this.setCustomerProfile(customer)
                      this.$refs.modalDocument.hide()
                    })
                  }
                ).catch(
                  ({ message }) => {
                    this.$refs.customerInformations.setErrors(message.errors)
                    this.$refs.customerInformations.setInformation({ status: 'New' })
                  }
                )
              }).catch(
                ({ message }) => {
                  this.$refs.attachmentUploader.setErrors(message.errors)
                  this.$refs.formDocument.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postDocument () {
      return await SAdminCustomerDocument.post(this.formDocument).then(
        ({ data }) => data
      )
    },

    ...mapActions({
      showCustomerSuggestions: 'customer-management/showCustomerSuggestionAction'
    })
  }
}
</script>
